import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { useQuery } from "convex/react";
import { useObservedMutation } from "@/hooks/use-observed-convex";

/**
 * Per-consultation projections for the clarification timeline: the collapsed
 * `displayStatus`, reply/transcript counts, the Teams deep link, the summary
 * message id, who cancelled and whether the viewer may cancel — everything the
 * timeline needs without re-deriving status, counting replies or checking roles
 * on the client.
 */
export function useConsultations(clarificationRequestId: Id<"clarificationRequests"> | null) {
  return useQuery(
    api.clarificationConsultations.api.listProjectionsByClarification,
    clarificationRequestId ? { clarificationRequestId } : "skip"
  );
}

export function useStartTeamsConsultation() {
  return useObservedMutation(api.clarificationConsultations.api.startTeamsConsultation, {
    feature: "clarification-consultations",
    operation: "start-teams-consultation",
    includeRequestId: true,
  });
}

export function useRetryConsultationSetup() {
  return useObservedMutation(api.clarificationConsultations.api.retryConsultationSetup, {
    feature: "clarification-consultations",
    operation: "retry-consultation-setup",
    includeRequestId: true,
  });
}

export function useCancelConsultation() {
  return useObservedMutation(api.clarificationConsultations.api.cancelConsultation, {
    feature: "clarification-consultations",
    operation: "cancel-consultation",
    includeRequestId: true,
  });
}

export function useConsultationTranscript(teamsChatId: Id<"teamsChats"> | null) {
  return useQuery(api.teams.messages.listByChat, teamsChatId ? { teamsChatId } : "skip");
}
